// src/pages/AdminBarManager.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatMainWithSecondary } from "../lib/currency";

/* ---------- base URLs (trim trailing slashes) ---------- */
const trim = (s) => (s || "").replace(/\/+$/g, "");
const API = trim(process.env.REACT_APP_API_BASE) || "";          // '' → same origin
const IMAGE_BASE = trim(process.env.REACT_APP_IMAGE_BASE) || ""; // '' → same origin

/* ---------- fetch helpers ---------- */
async function fetchJsonStrict(url, opts) {
  const res = await fetch(url, opts);
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status}${txt ? ` — ${txt}` : ""}`);
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : null;
}
async function fetchJsonSoft(url, opts) { try { return await fetchJsonStrict(url, opts); } catch { return null; } }

/* ---------- path helpers (DB keeps 'images/<file>') ---------- */
const normalizeDbPath = (p) => {
  if (!p) return "";
  let s = String(p).trim().replace(/\\/g, "/");
  s = s.replace(/^https?:\/\/[^/]+/i, "");
  s = s.replace(/^\/+/, "");
  const m = s.match(/(?:^|\/)(images\/.+)$/i);
  return m ? m[1] : s;
};
const dbToPreview = (p) => {
  if (!p) return "";
  const rel = normalizeDbPath(p);
  return `${IMAGE_BASE}/${rel}`.replace(/([^:]\/)\/+/g, "$1");
};

/* ---------- shared input style for light fields ---------- */
const INPUT_LIGHT =
  "bg-white/90 border border-white/40 text-slate-900 placeholder-slate-500 caret-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-400";

export default function AdminBarManager() {
  const navigate = useNavigate();

  /* ---------- state ---------- */
  const [cinemas, setCinemas] = useState([]);
  const [cinemaId, setCinemaId] = useState("");
  const [list, setList] = useState([]);
  const [flash, setFlash] = useState(null); // {t:"ok"|"err"|"info", m:string}

  const [form, setForm] = useState({
    article_id: null, article_name: "", article_type: "Напитка",
    price: "", quantity: "", image_path: "", active: true
  });
  const editing = useMemo(() => !!form.article_id, [form.article_id]);

  // сортиране на таблицата
  const [sort, setSort] = useState({ by: "article_name", dir: "asc" });

  // масов избор
  const [selected, setSelected] = useState(new Set());
  const allSelected = list.length > 0 && selected.size === list.length;

  // търсене/филтри
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // селектор на изображения
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQ, setPickerQ] = useState("");
  const [pickerItems, setPickerItems] = useState([]);
  const [pickerTargetId, setPickerTargetId] = useState(null);

  // качване
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // каталог (споделен)
  const [catalog, setCatalog] = useState([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogSel, setCatalogSel] = useState(new Set());
  const [catQuery, setCatQuery] = useState("");
  const [catDefaults, setCatDefaults] = useState({ price: "", quantity: "" });

  /* === динамични типове според текущия списък === */
  const typeOptions = useMemo(() => {
    const set = new Set(
      (list || [])
        .map(r => (r.article_type ?? "").toString().trim())
        .filter(Boolean)
    );
    const base = set.size ? Array.from(set) : ["Напитка", "Храна", "Друго"];
    const arr = base.sort((a, b) => a.localeCompare(b, "bg", { sensitivity: "accent" }));
    const cur = (form.article_type ?? "").toString().trim();
    if (cur && !arr.includes(cur)) arr.unshift(cur);
    return arr;
  }, [list, form.article_type]);

  /* ---------- load base data ---------- */
  useEffect(() => { (async () => {
    const rows = await fetchJsonSoft(`${API}/api/cinemas`);
    setCinemas(Array.isArray(rows) ? rows : []);
  })(); }, []);

  const refreshList = async (keepSelection = false) => {
    if (!cinemaId) { setList([]); setSelected(new Set()); return; }
    const rows = await fetchJsonSoft(`${API}/api/bar/articles/all/${cinemaId}`);
    setList(Array.isArray(rows) ? rows : []);
    if (!keepSelection) setSelected(new Set());
  };
  useEffect(() => { refreshList(); }, [cinemaId]);

  /* ---------- CRUD ---------- */
  const resetForm = () => setForm({
    article_id: null, article_name: "", article_type: typeOptions[0] || "Напитка",
    price: "", quantity: "", image_path: "", active: true
  });

  const startEdit = (row) => setForm({
    article_id: row.article_id,
    article_name: row.article_name || "",
    article_type: row.article_type || typeOptions[0] || "Напитка",
    price: String(row.price ?? ""),
    quantity: String(row.quantity ?? ""),
    image_path: normalizeDbPath(row.image_path || ""),
    active: !!row.active
  });

  const saveArticle = async (e) => {
    e?.preventDefault?.();
    if (!cinemaId) return toast("err", "Моля, изберете кино.");
    if (!form.article_name.trim()) return toast("err", "Въведете име на артикул.");

    const payload = {
      article_name: form.article_name.trim(),
      article_type : form.article_type.trim(),
      price: parseFloat(form.price || 0),
      quantity: parseInt(form.quantity || 0, 10),
      image_path: form.image_path ? normalizeDbPath(form.image_path) : null,
      active: !!form.active
    };

    try {
      if (editing) {
        await fetchJsonStrict(`${API}/api/bar/article/${form.article_id}`, {
          method:"PUT", headers:{ "Content-Type":"application/json" },
          body: JSON.stringify(payload)
        });
        toast("ok", "Актуализирано.");
      } else {
        await fetchJsonStrict(`${API}/api/bar/article`, {
          method:"POST", headers:{ "Content-Type":"application/json" },
          body: JSON.stringify({ cinema_id: Number(cinemaId), ...payload })
        });
        toast("ok", "Създаден артикул.");
      }
      await refreshList(true);
      resetForm();
    } catch (e2) {
      toast("err", e2.message || "Записът се провали.");
    }
  };

  const delArticle = async (row) => {
    if (!window.confirm(`Изтриване на „${row.article_name}“?`)) return;
    try {
      await fetchJsonStrict(`${API}/api/bar/article/${row.article_id}`, { method:"DELETE" });
      await refreshList(true);
      toast("ok", "Изтрито.");
    } catch (e) { toast("err", e.message || "Неуспешно изтриване."); }
  };

  const toggleActive = async (row) => {
    try {
      await fetchJsonStrict(`${API}/api/bar/article/${row.article_id}/active`, {
        method:"PATCH", headers:{ "Content-Type":"application/json" },
        body: JSON.stringify({ active: row.active ? 0 : 1 })
      });
      await refreshList(true);
    } catch {}
  };

  const setQty = async (row, qty) => {
    try {
      await fetchJsonStrict(`${API}/api/bar/article/${row.article_id}/quantity`, {
        method:"PATCH", headers:{ "Content-Type":"application/json" },
        body: JSON.stringify({ quantity: Number(qty) })
      });
      await refreshList(true);
    } catch { toast("err", "Грешка при обновяване на наличност."); }
  };

  const setPrice = async (row, price) => {
    try {
      await fetchJsonStrict(`${API}/api/bar/article/${row.article_id}`, {
        method:"PUT", headers:{ "Content-Type":"application/json" },
        body: JSON.stringify({ price: Number(price) })
      });
      await refreshList(true);
    } catch { toast("err", "Грешка при обновяване на цена."); }
  };

  /* ---------- selection helpers ---------- */
  const toggleRowSel = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (allSelected) return setSelected(new Set());
    setSelected(new Set(list.map(r => r.article_id)));
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Изтриване на ${selected.size} избрани?`)) return;
    try {
      for (const id of selected) {
        await fetchJsonStrict(`${API}/api/bar/article/${id}`, { method: "DELETE" });
      }
      await refreshList();
      toast("ok", `Изтрити: ${selected.size}`);
    } catch (e) { toast("err", e.message || "Bulk delete неуспешен."); }
  };
  const bulkRemoveImages = async () => {
    if (!selected.size) return;
    try {
      for (const id of selected) {
        await fetchJsonStrict(`${API}/api/bar/article/${id}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ image_path: null })
        });
      }
      await refreshList(true);
      toast("ok", `Премахнати изображения: ${selected.size}`);
    } catch (e) { toast("err", e.message || "Bulk remove images неуспешен."); }
  };

  /* ---------- static image picker ---------- */
  const openPicker = async (targetArticleId = null) => {
    const rows = await fetchJsonSoft(`${API}/api/bar/images`);
    setPickerItems(Array.isArray(rows) ? rows : []);
    setPickerQ("");
    setPickerTargetId(targetArticleId);
    setPickerOpen(true);
  };
  const searchPicker = async (val) => {
    setPickerQ(val);
    const rows = await fetchJsonSoft(`${API}/api/bar/images?q=${encodeURIComponent(val)}`);
    setPickerItems(Array.isArray(rows) ? rows : []);
  };
  const applyPickedImage = async (webPath) => {
    const dbPath = normalizeDbPath(webPath);
    if (pickerTargetId) {
      try {
        await fetchJsonStrict(`${API}/api/bar/article/${pickerTargetId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ image_path: dbPath })
        });
        await refreshList(true);
        toast("ok", "Изображението е обновено.");
      } catch (e) {
        toast("err", e.message || "Неуспешно обновяване на изображение.");
      } finally {
        setPickerOpen(false);
        setPickerTargetId(null);
      }
    } else {
      setForm(f => ({ ...f, image_path: dbPath }));
      setPickerOpen(false);
    }
  };

  /* ---------- upload ---------- */
  const doUpload = async () => {
    if (!uploadFile) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("file", uploadFile);
      if (form.article_id) fd.append("article_id", String(form.article_id));
      const res = await fetch(`${API}/api/bar/upload`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || "Качването се провали.");
      setForm((f) => ({ ...f, image_path: normalizeDbPath(data.image_path) }));
      setUploadFile(null);
      toast("ok", "Изображението е качено.");
      if (form.article_id && cinemaId) await refreshList(true);
    } catch (e) {
      toast("err", e.message || "Качването се провали.");
    } finally {
      setUploading(false);
    }
  };

  /* ---------- catalog (shared) ---------- */
  const loadCatalog = async () => {
    if (catalog.length) { setCatalogOpen(true); return; }
    setCatalogLoading(true);

    const cins = await fetchJsonSoft(`${API}/api/cinemas`);
    const agg = new Map(); // key: lower(article_name) → {name,type,image,sum,count}

    for (const c of (cins || [])) {
      const id = c.cinema_id ?? c.CINEMA_ID;
      if (!id) continue;
      const rows = await fetchJsonSoft(`${API}/api/bar/articles/all/${id}`);
      for (const r of rows || []) {
        const name = (r.article_name || "").trim();
        if (!name) continue;
        const key = name.toLowerCase();
        const price = Number(r.price);
        if (!agg.has(key)) {
          agg.set(key, {
            article_name: name,
            article_type: r.article_type || "Друго",
            image_path: r.image_path || "",
            sum: 0, count: 0
          });
        }
        if (Number.isFinite(price) && price > 0) {
          const a = agg.get(key);
          a.sum += price; a.count += 1;
        }
      }
    }

    const result = Array.from(agg.values()).map(a => ({
      article_name: a.article_name,
      article_type : a.article_type,
      image_path   : a.image_path,
      suggestedPrice: a.count ? +(a.sum / a.count).toFixed(2) : undefined
    })).sort((x, y) => x.article_name.localeCompare(y.article_name, "bg"));

    setCatalog(result);
    setCatalogLoading(false);
    setCatalogOpen(true);
  };

  const toggleCatSel = (name) => {
    const key = name.toLowerCase();
    setCatalogSel(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const selectAllVisible = (filtered) => {
    const next = new Set(catalogSel);
    const currentNames = new Set((list || []).map(r => (r.article_name || "").toLowerCase().trim()));
    for (const it of filtered) {
      const key = it.article_name.toLowerCase();
      if (!currentNames.has(key)) next.add(key);
    }
    setCatalogSel(next);
  };
  const clearSelection = () => setCatalogSel(new Set());

  const addFromCatalog = async () => {
    if (!cinemaId || !catalogSel.size) return;
    const existingNames = new Set((list || []).map(r => (r.article_name || "").toLowerCase().trim()));
    const defPrice = catDefaults.price === "" ? null : parseFloat(catDefaults.price);
    const defQty   = catDefaults.quantity === "" ? null : parseInt(catDefaults.quantity, 10);

    let added = 0;
    for (const key of catalogSel) {
      if (existingNames.has(key)) continue;
      const item = catalog.find(x => x.article_name.toLowerCase() === key);
      if (!item) continue;

      const priceToUse =
        defPrice !== null && Number.isFinite(defPrice)
          ? defPrice
          : (Number.isFinite(item.suggestedPrice) ? item.suggestedPrice : 0);

      const qtyToUse =
        defQty !== null && Number.isFinite(defQty) ? defQty : 0;

      await fetchJsonStrict(`${API}/api/bar/article`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cinema_id: Number(cinemaId),
          article_name: item.article_name,
          article_type: item.article_type || "Друго",
          price: priceToUse,
          quantity: qtyToUse,
          image_path: item.image_path ? normalizeDbPath(item.image_path) : null,
          active: true
        })
      });
      added++;
    }

    await refreshList();
    setCatalogSel(new Set());
    setCatalogOpen(false);
    toast("ok", `Добавени: ${added}`);
  };

  /* ---------- helpers ---------- */
  const toast = (t, m) => {
    setFlash({ t, m });
    setTimeout(() => setFlash(null), 2600);
  };

  const sortedFiltered = useMemo(() => {
    const filt = (list || []).filter(r => {
      const text = (q || "").toLowerCase().trim();
      const t = (r.article_type || "").toLowerCase();
      const n = (r.article_name || "").toLowerCase();
      const okText = !text || n.includes(text);
      const okType = !typeFilter || t === typeFilter.toLowerCase();
      return okText && okType;
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filt].sort((a, b) => {
      const A = a[sort.by]; const B = b[sort.by];
      if (sort.by === "price" || sort.by === "quantity") {
        return (Number(A) - Number(B)) * dir;
      }
      return String(A ?? "").localeCompare(String(B ?? ""), "bg") * dir;
    });
  }, [list, q, typeFilter, sort]);

  const headerSort = (by) => {
    setSort((s) => (s.by === by ? { by, dir: s.dir === "asc" ? "desc" : "asc" } : { by, dir: "asc" }));
  };

  /* ---------- UI ---------- */
  return (
    <div className="min-h-screen relative p-4 md:p-6 [color-scheme:light]">
      {/* Фон с пурпурен воал */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <img
          src="https://latonacinema.com/wp-content/uploads/2025/06/cinema-hall-with-rows-of-grey-seats-in-darkness.jpg"
          alt="фон"
          className="h-full w-full object-cover blur-[2px] scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2c204f]/80 via-black/60 to-[#6c56b5]/70" />
      </div>

      {/* Хедър */}
      <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3 mb-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 text-white border border-white/20 hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-purple-400/60"
          title="Назад"
        >
          ← Назад
        </button>
        <div className="flex items-center gap-3">
          <img
            src="https://latonacinema.com/wp-content/uploads/2025/06/cropped-latonacinema.png"
            alt="Лого"
            className="h-10"
          />
          <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
            🍿 Бар — Администрация
          </h1>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <select
            className={`px-3 py-2 rounded-xl ${INPUT_LIGHT}`}
            value={cinemaId}
            onChange={e => setCinemaId(e.target.value)}
          >
            <option value="">🎬 Изберете кино</option>
            {Array.isArray(cinemas) && cinemas.map(c => (
              <option key={c.cinema_id ?? c.CINEMA_ID} value={c.cinema_id ?? c.CINEMA_ID}>
                {c.cinema_name ?? c.CINEMA_NAME ?? c.name ?? c.company_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Карта */}
      <div className="max-w-7xl mx-auto bg-white/8 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-4 md:p-6">
        {/* Форма за създаване/редакция */}
        <form className="grid gap-3 mb-6" onSubmit={saveArticle}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              className={`px-3 py-2 rounded-xl ${INPUT_LIGHT}`}
              placeholder="Име на артикул"
              value={form.article_name}
              onChange={e => setForm(f => ({ ...f, article_name: e.target.value }))}
            />
            <select
              className={`px-3 py-2 rounded-xl ${INPUT_LIGHT}`}
              value={form.article_type}
              onChange={e => setForm(f => ({ ...f, article_type: e.target.value }))}
            >
              {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <div className="flex items-center gap-2">
              <input
                type="number" step="0.01"
                className={`flex-1 px-3 py-2 rounded-xl ${INPUT_LIGHT}`}
                placeholder="Цена (лв.)"
                value={form.price}
                onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
              />
              <button
                type="button"
                className="px-2 py-2 rounded-lg bg-white/20 text-white border border-white/20"
                onClick={() => setForm(f => ({ ...f, price: String((+f.price || 0) - 0.50) }))}
                title="-0.50 лв"
              >−</button>
              <button
                type="button"
                className="px-2 py-2 rounded-lg bg-white/20 text-white border border-white/20"
                onClick={() => setForm(f => ({ ...f, price: String((+f.price || 0) + 0.50) }))}
                title="+0.50 лв"
              >+</button>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                className={`flex-1 px-3 py-2 rounded-xl ${INPUT_LIGHT}`}
                placeholder="Наличност"
                value={form.quantity}
                onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
              />
              <button
                type="button"
                className="px-2 py-2 rounded-lg bg-white/20 text-white border border-white/20"
                onClick={() => setForm(f => ({ ...f, quantity: String((+f.quantity || 0) - 1) }))}
                title="-1"
              >−</button>
              <button
                type="button"
                className="px-2 py-2 rounded-lg bg-white/20 text-white border border-white/20"
                onClick={() => setForm(f => ({ ...f, quantity: String((+f.quantity || 0) + 1) }))}
                title="+1"
              >+</button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              className={`px-3 py-2 rounded-xl ${INPUT_LIGHT}`}
              placeholder='Път до изображение (записва се като "images/…")'
              value={form.image_path}
              onChange={e => setForm(f => ({ ...f, image_path: normalizeDbPath(e.target.value) }))}
            />
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/20 text-white border border-white/20">
              <input
                type="checkbox"
                checked={form.active}
                onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
              />
              Активен
            </label>

            <div className="flex items-center gap-2">
              <button type="button" onClick={() => openPicker(null)}
                className="w-full px-3 py-2 rounded-xl bg-white/10 text-white border border-white/20 hover:bg-white/15">
                📁 Избери изображение
              </button>
              {form.image_path && (
                <a className="px-3 py-2 rounded-xl bg-white/10 text-white border border-white/20 hover:bg-white/15"
                   href={dbToPreview(form.image_path)} target="_blank" rel="noreferrer">
                  Преглед
                </a>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="file" accept="image/*"
                onChange={e => setUploadFile(e.target.files?.[0] || null)}
                className={`flex-1 rounded-xl ${INPUT_LIGHT} file:mr-2 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-purple-600 file:text-white`}
              />
              <button
                type="button"
                disabled={!uploadFile || uploading}
                onClick={doUpload}
                className="px-3 py-2 rounded-xl bg-emerald-600 text-white disabled:opacity-60"
              >
                ⬆ Качи {uploading ? "…" : ""}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button type="submit" className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
              {editing ? "💾 Запази" : "➕ Създай"}
            </button>
            {editing && (
              <button type="button" onClick={resetForm} className="px-4 py-2 rounded-xl bg-white/10 text-white border border-white/20">
                Отказ
              </button>
            )}
            <button type="button" onClick={loadCatalog}
              className="ml-auto px-4 py-2 rounded-xl bg-gradient-to-br from-[#6c56b5] to-[#2c204f] text-white border border-white/20">
              📚 Добави от каталог
            </button>
          </div>
        </form>

        {/* Лепкав тулбар за търсене/масови действия */}
        <div className="sticky top-2 z-10 mb-3 rounded-xl bg-white/10 border border-white/20 backdrop-blur px-3 py-2 flex flex-wrap items-center gap-2">
          <input
            className={`px-3 py-2 rounded-lg ${INPUT_LIGHT}`}
            placeholder="Търси по име…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select
            className={`px-3 py-2 rounded-lg ${INPUT_LIGHT}`}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">Всички типове</option>
            {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          {selected.size > 0 && (
            <>
              <span className="text-white/80 text-sm ml-2">{selected.size} избрани</span>
              <button className="px-3 py-2 rounded-lg bg-rose-600 text-white" onClick={bulkDelete}>Изтрий избраните</button>
              <button className="px-3 py-2 rounded-lg bg-white/10 text-white border border-white/20" onClick={bulkRemoveImages}>
                Премахни изображения
              </button>
            </>
          )}
        </div>

        {/* Таблица */}
        <div className="overflow-x-auto">
          <table className="min-w-full rounded-2xl overflow-hidden">
            <thead className="bg-white/10 text-white">
              <tr>
                <Th w="w-10">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                </Th>
                <Th>Изображение</Th>
                <Th onClick={() => headerSort("article_name")} sortable dir={sort.by === "article_name" ? sort.dir : null}>Име</Th>
                <Th onClick={() => headerSort("article_type")} sortable dir={sort.by === "article_type" ? sort.dir : null}>Тип</Th>
                <Th onClick={() => headerSort("price")} sortable dir={sort.by === "price" ? sort.dir : null}>Цена</Th>
                <Th onClick={() => headerSort("quantity")} sortable dir={sort.by === "quantity" ? sort.dir : null}>Наличност</Th>
                <Th>Статус</Th>
                <Th>Действия</Th>
              </tr>
            </thead>
            <tbody className="bg-white/5">
              {sortedFiltered.map(row => {
                const isSel = selected.has(row.article_id);
                return (
                  <tr key={row.article_id} className={`border-b border-white/10 ${isSel ? "bg-white/10" : "hover:bg-white/8"}`}>
                    <Td>
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggleRowSel(row.article_id)}
                      />
                    </Td>
                    <Td>
                      {row.image_path ? (
                        <img
                          src={dbToPreview(row.image_path)}
                          alt={row.article_name}
                          className="w-16 h-16 object-contain rounded-lg bg-white/10"
                        />
                      ) : <span className="text-white/50">—</span>}
                    </Td>
                    <Td className="text-white">{row.article_name}</Td>
                    <Td className="text-white/90">{row.article_type}</Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          step="0.01"
                          className={`w-28 px-2 py-1 rounded-lg ${INPUT_LIGHT}`}
                          defaultValue={Number(row.price ?? 0).toFixed(2)}
                          onBlur={e => {
                            const v = parseFloat(e.target.value || 0);
                            if (!Number.isFinite(v)) return;
                            setPrice(row, v);
                          }}
                        />
                        <button className="px-2 py-1 rounded bg-white/10 text-white border border-white/20"
                                onClick={() => setPrice(row, Number(row.price || 0) - 0.5)} title="-0.50">−</button>
                        <button className="px-2 py-1 rounded bg-white/10 text-white border border-white/20"
                                onClick={() => setPrice(row, Number(row.price || 0) + 0.5)} title="+0.50">+</button>
                      </div>
                    </Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          className={`w-24 px-2 py-1 rounded-lg ${INPUT_LIGHT}`}
                          defaultValue={row.quantity}
                          onBlur={e => {
                            const v = parseInt(e.target.value || 0, 10);
                            if (!Number.isFinite(v)) return;
                            setQty(row, v);
                          }}
                        />
                        <button className="px-2 py-1 rounded bg-white/10 text-white border border-white/20"
                                onClick={() => setQty(row, Number(row.quantity || 0) - 1)} title="-1">−</button>
                        <button className="px-2 py-1 rounded bg-white/10 text-white border border-white/20"
                                onClick={() => setQty(row, Number(row.quantity || 0) + 1)} title="+1">+</button>
                      </div>
                    </Td>
                    <Td>
                      <button onClick={() => toggleActive(row)}
                              className={`px-2 py-1 rounded-lg border ${row.active ? "bg-emerald-600 text-white border-emerald-500" : "bg-white/10 text-white border-white/20"}`}>
                        {row.active ? "Активен" : "Неактивен"}
                      </button>
                    </Td>
                    <Td>
                      <div className="flex flex-wrap gap-2">
                        <button className="px-3 py-1.5 rounded-lg bg-blue-600 text-white" onClick={() => startEdit(row)}>Редакция</button>
                        <button className="px-3 py-1.5 rounded-lg bg-rose-600 text-white" onClick={() => delArticle(row)}>Изтрий</button>
                        <button className="px-3 py-1.5 rounded-lg bg-white/10 text-white border border-white/20" onClick={() => openPicker(row.article_id)}>Промени изображение</button>
                        {row.image_path && (
                          <button
                            className="px-3 py-1.5 rounded-lg bg-white/10 text-white border border-white/20"
                            title="Премахни изображение"
                            onClick={async () => {
                              await fetchJsonStrict(`${API}/api/bar/article/${row.article_id}`, {
                                method: "PUT",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ image_path: null })
                              });
                              await refreshList(true);
                              toast("ok", "Изображението е премахнато.");
                            }}
                          >
                            Премахни изображение
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })}
              {!sortedFiltered.length && (
                <tr>
                  <td className="p-4 text-white/70" colSpan={8}>Няма артикули.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Тост съобщение */}
      {flash && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-xl shadow-2xl text-white ${flash.t === "ok" ? "bg-emerald-600" : flash.t === "err" ? "bg-rose-600" : "bg-slate-700"}`}>
          {flash.m}
        </div>
      )}

      {/* Селектор на изображения */}
      {pickerOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
          <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center gap-2 p-4 border-b">
              <h3 className="text-lg font-bold flex-1">Изберете изображение</h3>
              <button
                className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                onClick={() => { setPickerOpen(false); setPickerTargetId(null); }}
              >
                Затвори
              </button>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <input
                  className="border p-2 rounded w-full text-slate-900 placeholder-slate-500 caret-slate-900"
                  placeholder="Търсене…"
                  value={pickerQ}
                  onChange={e => searchPicker(e.target.value)}
                />
              </div>
              <div className="overflow-auto" style={{ maxHeight: "65vh" }}>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {pickerItems.map(it => (
                    <button
                      key={it.path}
                      onClick={() => applyPickedImage(it.path)}
                      className="border rounded-xl p-2 hover:shadow text-left bg-white"
                      title={it.name}
                    >
                      <img src={`${IMAGE_BASE}${it.path}`} alt={it.name} className="w-full h-28 object-contain mb-1" />
                      <div className="text-xs truncate">{it.name}</div>
                    </button>
                  ))}
                  {!pickerItems.length && <div className="text-gray-500 col-span-full">Няма файлове.</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модал — Каталог */}
      {catalogOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
          <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            <div className="flex items-center gap-2 p-4 border-b">
              <h3 className="text-lg font-bold flex-1">Добавяне от каталог</h3>
              <button className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200" onClick={() => setCatalogOpen(false)}>Затвори</button>
            </div>

            <div className="p-4 flex flex-wrap items-center gap-3">
              <input
                className="border p-2 rounded flex-1 min-w-[220px] text-slate-900 placeholder-slate-500 caret-slate-900"
                placeholder="Търси по име…"
                value={catQuery}
                onChange={(e) => setCatQuery(e.target.value)}
              />
              <input
                type="number"
                step="0.01"
                className="border p-2 rounded w-36 text-slate-900 placeholder-slate-500 caret-slate-900"
                placeholder="Цена по подразб."
                value={catDefaults.price}
                onChange={(e) => setCatDefaults((d) => ({ ...d, price: e.target.value }))}
                title="Цена за нови артикули (празно → предлага се)"
              />
              <input
                type="number"
                className="border p-2 rounded w-28 text-slate-900 placeholder-slate-500 caret-slate-900"
                placeholder="Количество"
                value={catDefaults.quantity}
                onChange={(e) => setCatDefaults((d) => ({ ...d, quantity: e.target.value }))}
                title="Количество за нови артикули"
              />
              <button
                className="px-3 py-2 rounded-lg bg-white border"
                onClick={() => {
                  const filtered = catalog.filter(it =>
                    !catQuery.trim() || it.article_name.toLowerCase().includes(catQuery.toLowerCase())
                  );
                  selectAllVisible(filtered);
                }}
              >
                Маркирай видимите
              </button>
              <button className="px-3 py-2 rounded-lg bg-white border" onClick={clearSelection}>Изчисти</button>
              <button
                className="px-3 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-60"
                disabled={!catalogSel.size || !cinemaId}
                onClick={addFromCatalog}
              >
                Добави избраните ({catalogSel.size})
              </button>
            </div>

            <div className="p-4 pt-0 overflow-auto" style={{ maxHeight: "70vh" }}>
              {catalogLoading ? (
                <div className="p-8 text-center">Зареждане на каталога…</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {catalog
                    .filter(it => !catQuery.trim() || it.article_name.toLowerCase().includes(catQuery.toLowerCase()))
                    .map(it => {
                      const key = it.article_name.toLowerCase();
                      const already = (list || []).some(r => (r.article_name || "").toLowerCase().trim() === key);
                      const checked = catalogSel.has(key);
                      return (
                        <label key={key}
                          className={`border rounded-xl p-3 flex gap-3 items-center bg-white ${already ? "opacity-60" : "hover:shadow"}`}>
                          <input type="checkbox" disabled={already} checked={checked} onChange={() => toggleCatSel(it.article_name)} />
                          <img
                            src={it.image_path ? dbToPreview(it.image_path) : ""}
                            alt=""
                            className="w-16 h-16 object-contain bg-gray-50 rounded"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                          <div className="min-w-0">
                            <div className="font-semibold truncate">{it.article_name}</div>
                            <div className="text-xs text-gray-600">{it.article_type || "Друго"}</div>
                            {Number.isFinite(it.suggestedPrice) && (
                              <div className="text-xs text-emerald-700">Предложена: {formatMainWithSecondary(it.suggestedPrice)}</div>
                            )}
                            {already && <div className="text-xs text-green-700">Вече съществува в това кино</div>}
                          </div>
                        </label>
                      );
                    })}
                  {!catalog.length && <div className="text-gray-500 col-span-full">Все още няма елементи в каталога.</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- малки UI компоненти ---------- */
function Th({ children, onClick, sortable = false, dir = null, w }) {
  return (
    <th
      className={`p-2 text-left text-white/90 font-semibold ${w || ""} ${sortable ? "cursor-pointer select-none" : ""}`}
      onClick={onClick}
      title={sortable ? "Сортирай" : undefined}
    >
      <div className="inline-flex items-center gap-1">
        {children}
        {dir === "asc" && <span>▲</span>}
        {dir === "desc" && <span>▼</span>}
      </div>
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`p-2 align-middle text-white/90 ${className}`}>{children}</td>;
}
